import './metaHackathon.scss';
import { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { MdShowChart, MdTableChart } from 'react-icons/md';
import { Page } from '@Components/Page';
import { Button } from '@Components/Button';
import { ChartContainer } from '@Components/ChartContainer';
import { DatePicker } from '@Components/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import { Appearance, Design, Size } from '@Components/designConstants';
import { FullPageSpinner } from '@Components/Spinner';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { useSetError } from '@src/redux/modals';
import { awaitJSON, MetaLambdaFetch } from '@src/utils/fetch-utils';
import {
  ColorMode,
  ColorModeType,
  CreativeDiversityInsight,
  CreativeFatigueInsight,
  PageTab,
  PageTabType,
  ViewMode,
  ViewModeType
} from './metaHackathon-constants';
import { buildAssetColorMap, buildAssetSeries, sliceBundleToRange } from './metaHackathon-utils';
import {
  AdPerformanceRow,
  PERFORMANCE_COLUMNS,
  PerformanceColumnKey,
  fetchSocialAdPerformance
} from './metaHackathon-performance';
import { QuadrantScatterChart } from './QuadrantScatterChart';
import { MetaDataTable } from './MetaDataTable';
import { PlaybackControls } from './PlaybackControls';
import { ChartColorControls } from './ChartColorControls';
import { useTimelinePlayback } from './useTimelinePlayback';
import { CreativeSuite } from './CreativeSuite/CreativeSuite';

const PAGE_TABS: { key: PageTabType; label: string }[] = [
  { key: PageTab.SUITE, label: 'Creative Suite' },
  { key: PageTab.EXPLORER, label: 'Interactive Explorer' }
];

const PageTabs = ({
  active,
  onChange
}: {
  active: PageTabType;
  onChange: (tab: PageTabType) => void;
}) => (
  <div className='page-tabs'>
    {PAGE_TABS.map(tab => (
      <InteractiveDiv
        key={tab.key}
        className={cn('page-tab', { active: active === tab.key })}
        onClick={() => onChange(tab.key)}
      >
        {tab.label}
      </InteractiveDiv>
    ))}
  </div>
);

const ViewToggle = ({
  viewMode,
  onChange
}: {
  viewMode: ViewModeType;
  onChange: (mode: ViewModeType) => void;
}) => (
  <div className='view-toggle'>
    <Button
      size={Size.SM}
      design={Design.NEUTRAL}
      appearance={viewMode === ViewMode.CHART ? Appearance.FILLED : Appearance.OUTLINED}
      onClick={() => onChange(ViewMode.CHART)}
    >
      <MdShowChart /> Chart
    </Button>
    <Button
      size={Size.SM}
      design={Design.NEUTRAL}
      appearance={viewMode === ViewMode.TABLE ? Appearance.FILLED : Appearance.OUTLINED}
      onClick={() => onChange(ViewMode.TABLE)}
    >
      <MdTableChart /> Table
    </Button>
  </div>
);

const fetchCreativeFatigueInsights = async (): Promise<CreativeFatigueInsight[]> => {
  const res = await MetaLambdaFetch('/getCreativeFatigueInsights');
  return awaitJSON<CreativeFatigueInsight[]>(res);
};

const fetchCreativeDiversityInsights = async (): Promise<CreativeDiversityInsight[]> => {
  const res = await MetaLambdaFetch('/getCreativeDiversityInsights');
  return awaitJSON<CreativeDiversityInsight[]>(res);
};

interface MetaInsights {
  fatigue: CreativeFatigueInsight[];
  diversity: CreativeDiversityInsight[];
}

export const MetaHackathon = () => {
  const setError = useSetError();
  const [insights, setInsights] = useState<MetaInsights | undefined>();
  const [performance, setPerformance] = useState<Map<string, AdPerformanceRow>>();

  useEffect(() => {
    (async () => {
      try {
        const [fatigue, diversity] = await Promise.all([
          fetchCreativeFatigueInsights(),
          fetchCreativeDiversityInsights()
        ]);
        setInsights({ fatigue, diversity });
      } catch (e) {
        setError({ message: 'Failed to fetch Meta creative insights', reportError: e });
      }
    })();
  }, [setError]);

  const fullBundle = useMemo(
    () => buildAssetSeries(insights?.fatigue ?? [], insights?.diversity ?? []),
    [insights]
  );

  const minDate = fullBundle.dates[0] ?? '';
  const maxDate = fullBundle.dates[fullBundle.dates.length - 1] ?? '';

  const [dateRange, setDateRange] = useState<DateRange>();
  const effectiveRange = useMemo(
    () => dateRange ?? { start: minDate, end: maxDate },
    [dateRange, minDate, maxDate]
  );

  // Selecting a range scopes the playback window — slice the timeline (and each
  // asset's points) to the chosen dates.
  const { series, dates } = useMemo(
    () => sliceBundleToRange(fullBundle, effectiveRange),
    [fullBundle, effectiveRange]
  );

  // Join ad-level Social performance by ad_group_id (= ad set id), the same id
  // our adsets are keyed by. Mock data won't match. Best-effort: failures don't block.
  useEffect(() => {
    const { series: allSeries, dates: allDates, usedMock: isMock } = fullBundle;
    if (isMock || allSeries.length === 0 || allDates.length === 0) return;

    (async () => {
      try {
        const byAdGroupId = await fetchSocialAdPerformance({
          start: allDates[0],
          end: allDates[allDates.length - 1]
        });

        // --- TEMP campaign-join diagnostics (remove once the join is confirmed) ---
        const scpIds = [...byAdGroupId.keys()];
        const matched = allSeries.filter(s => byAdGroupId.has(s.assetId));
        console.info(
          `[meta-hackathon] adsets=${allSeries.length}, SCP ad_group_id rows=${scpIds.length}, matched=${matched.length}`
        );
        console.info(
          '[meta-hackathon] sample adset ids:',
          allSeries.slice(0, 5).map(s => s.assetId)
        );
        console.info('[meta-hackathon] sample SCP ad_group_ids:', scpIds.slice(0, 5));
        if (matched.length === 0) {
          console.warn(
            scpIds.length === 0
              ? '[meta-hackathon] SCP returned 0 rows — check company/preset/date range.'
              : '[meta-hackathon] SCP returned rows but no ad_group_id matched our adset ids — id-space mismatch (insights ad account vs SCP company?).'
          );
        }

        setPerformance(byAdGroupId);
      } catch (e) {
        setError({ title: "Couldn't load ad performance", message: e.message, reportError: e });
      }
    })();
  }, [fullBundle, setError]);

  const playback = useTimelinePlayback(dates.length);

  const [viewMode, setViewMode] = useState<ViewModeType>(ViewMode.CHART);
  const [visibleCols, setVisibleCols] = useState<Record<PerformanceColumnKey, boolean>>(
    () =>
      Object.fromEntries(PERFORMANCE_COLUMNS.map(col => [col.key, true])) as Record<
        PerformanceColumnKey,
        boolean
      >
  );
  const toggleCol = useCallback(
    (key: PerformanceColumnKey) => setVisibleCols(prev => ({ ...prev, [key]: !prev[key] })),
    []
  );

  const [colorMode, setColorMode] = useState<ColorModeType>(ColorMode.CAMPAIGN);
  const [hiddenAssetIds, setHiddenAssetIds] = useState<Set<string>>(new Set());
  const toggleAsset = useCallback(
    (assetId: string) =>
      setHiddenAssetIds(prev => {
        const next = new Set(prev);
        if (next.has(assetId)) {
          next.delete(assetId);
        } else {
          next.add(assetId);
        }
        return next;
      }),
    []
  );

  const assetColors = useMemo(
    () => buildAssetColorMap(series, colorMode, performance),
    [series, colorMode, performance]
  );

  const [pageTab, setPageTab] = useState<PageTabType>(PageTab.SUITE);
  const showExplorer = pageTab === PageTab.EXPLORER;

  return (
    <Page
      title='Meta Hackathon'
      pageType='Meta Hackathon'
      actions={
        showExplorer && insights ? (
          <DatePicker
            range={effectiveRange}
            onChange={({ start, end }) => setDateRange({ start, end })}
            isOutsideRange={date => date < minDate || date > maxDate}
            minDate={minDate}
            maxDate={maxDate}
            datePresets
            design={Design.BRAND}
          />
        ) : undefined
      }
    >
      <div className='meta-hackathon'>
        <PageTabs active={pageTab} onChange={setPageTab} />

        {!showExplorer && <CreativeSuite />}

        {showExplorer &&
          (insights ? (
            <ChartContainer
              title='Creative Diversity vs. Fatigue'
              beforeTooltipText='Each point is an asset at the selected date — play to watch it move.'
              rightActions={<ViewToggle viewMode={viewMode} onChange={setViewMode} />}
              viewRightActions
            >
              <div className='chart-and-controls'>
                {viewMode === ViewMode.CHART ? (
                  <>
                    <QuadrantScatterChart
                      series={series}
                      cursor={playback.cursor}
                      dates={dates}
                      assetColors={assetColors}
                      hiddenAssetIds={hiddenAssetIds}
                      performance={performance}
                    />
                    <PlaybackControls dates={dates} playback={playback} />
                    <ChartColorControls
                      series={series}
                      colorMode={colorMode}
                      onColorModeChange={setColorMode}
                      assetColors={assetColors}
                      hiddenAssetIds={hiddenAssetIds}
                      onToggleAsset={toggleAsset}
                    />
                  </>
                ) : (
                  <MetaDataTable
                    series={series}
                    performance={performance}
                    visibleCols={visibleCols}
                    onToggleCol={toggleCol}
                  />
                )}
              </div>
            </ChartContainer>
          ) : (
            <FullPageSpinner />
          ))}
      </div>
    </Page>
  );
};
